/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { Timestamp } from 'firebase/firestore';

import Project, { projectSchema } from './project.js';

export const internalProjectSchema = projectSchema.shape({
  description: Yup.string().required('Description is required for internal projects'),
});

/**
 * @typedef {Yup.InferType<typeof internalProjectSchema>} InternalProjectSchema
 * @typedef {BaseDocData & InternalProjectSchema & { createdAt: Timestamp; }} InternalProjectDocData
*/

/**
 * Class representing an internal project.
 * @class
 * @extends Project
 */
export default class InternalProject extends Project {
  /** @type {InternalProjectDocData['description']} */ description;
  /**
   * Create an internal project.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  constructor({ description, ...props }) {
    const validatedData = internalProjectSchema.validateSync({ description, ...props });

    super(validatedData);

    this.description = validatedData.description;
  }

  /**
   * Converts the {@link InternalProject} instance to Firestore document data.
   * @returns {Omit<InternalProjectDocData, 'id'>} {@link InternalProjectDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      description: this.description,
      isInternal: true,
    };
  }

  /**
   * Creates an {@link InternalProject} instance from Firestore document data.
   * @param {any} docData - Raw Firestore document.
   * @returns An {@link InternalProject} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    return new InternalProject(docData);
  }
}
